/**
 * @quotech/ui — ConfirmDialog (controlled + imperative)
 *
 * Themed, accessible confirmation modal that replaces window.confirm
 * across every tenant. Two APIs in one module:
 *
 *   1. <ConfirmDialog open onConfirm onCancel /> — controlled
 *      React component. Pair with your own useState('open').
 *
 *   2. await confirmDialog({title, description, ...}) — imperative
 *      Promise<boolean>. Mounts the same component via createRoot
 *      portal so callers don't have to manage open state.
 *
 *   3. await alertDialog({title, description}) — imperative
 *      Promise<void>. Single OK button, no Cancel.
 *
 * Lifted from apps/sam (controlled visual) + apps/crm (imperative
 * API) on 2026-05-04 in qf!2472. The takeoff doc (L3.2) called for
 * 4 implementations → 1; this MR consolidates SAM + CRM.
 *
 * Phase 2 (separate MRs) will migrate the remaining two:
 *   - apps/quote/src/components/builder/modals/ConfirmDialog.tsx
 *     (hook-based useConfirmDialog — owned by the quote builder
 *     team; needs their sign-off before re-pointing)
 *   - apps/cfo/src/pages/cashflow/ConfirmDialog.tsx
 *     (CFO-local cashflow-delete dialog; trivial migration but
 *     bundled with the broader CFO cleanup in L3.5)
 *
 * USAGE (controlled):
 *   import { ConfirmDialog } from '@quotech/ui'
 *   const [open, setOpen] = useState(false)
 *   <ConfirmDialog
 *     open={open}
 *     title="Delete this job?"
 *     description="This is irreversible."
 *     variant="danger"
 *     onConfirm={() => { delete(); setOpen(false) }}
 *     onCancel={() => setOpen(false)}
 *   />
 *
 * USAGE (imperative):
 *   import { confirmDialog } from '@quotech/ui'
 *   if (await confirmDialog({ title: 'Delete?', description: '…',
 *                              variant: 'danger' })) {
 *     await deleteIt()
 *   }
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { createRoot } from 'react-dom/client'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { cn } from '../lib/utils'

export type ConfirmDialogVariant =
  | 'default'
  | 'danger'
  | 'info'
  | 'warning'
  | 'success'

export interface ConfirmDialogProps {
  open: boolean
  title: string
  /** Body text. Accepts a string or any ReactNode (e.g. a list,
   * an inline link, etc.). Aliased as `message` in some tenant
   * APIs — pass through whichever the consumer supplies. */
  description?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Visual emphasis. `danger` = red Confirm button + warn icon.
   * `warning` = amber. `success` = emerald. `info`/`default` =
   * primary blue. */
  variant?: ConfirmDialogVariant
  /** @deprecated Use `variant: 'danger'` instead. Kept for sam compat. */
  destructive?: boolean
  onConfirm: () => void
  onCancel: () => void
}

interface VariantConfig {
  icon: LucideIcon
  iconBg: string
  iconColor: string
  confirmClass: string
}

const VARIANT_CONFIG: Record<ConfirmDialogVariant, VariantConfig> = {
  default: {
    icon: AlertTriangle,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    confirmClass: 'bg-primary text-primary-foreground hover:bg-primary/90',
  },
  info: {
    icon: Info,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    confirmClass: 'bg-primary text-primary-foreground hover:bg-primary/90',
  },
  danger: {
    icon: AlertCircle,
    iconBg: 'bg-destructive/10',
    iconColor: 'text-destructive',
    confirmClass:
      'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
    confirmClass: 'bg-amber-600 text-white hover:bg-amber-700',
  },
  success: {
    icon: CheckCircle2,
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    confirmClass: 'bg-emerald-600 text-white hover:bg-emerald-700',
  },
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant,
  destructive,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // Backwards compat: SAM previously used `destructive: boolean`. If
  // either signal says danger, override variant to danger.
  const effectiveVariant: ConfirmDialogVariant =
    destructive === true ? 'danger' : variant ?? 'default'
  const config = VARIANT_CONFIG[effectiveVariant]
  const Icon = config.icon

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
      if (e.key === 'Enter') onConfirm()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onCancel, onConfirm])

  if (!open) return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-[2px] animate-in fade-in duration-150"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl border border-border bg-card shadow-2xl animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-5">
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
              config.iconBg,
              config.iconColor,
            )}
          >
            <Icon className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold text-foreground">{title}</p>
            {description ? (
              <div className="mt-1 text-[13px] leading-snug text-muted-foreground">
                {description}
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/20 px-4 py-2.5">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-border bg-card px-3 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            autoFocus
            onClick={onConfirm}
            className={cn(
              'rounded-lg px-3 py-1.5 text-[12px] font-semibold shadow-sm transition-colors',
              config.confirmClass,
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/* ── Imperative API ─────────────────────────────────────── */

export interface ConfirmDialogOptions {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: ConfirmDialogVariant
}

/**
 * Imperative confirm — returns a Promise<boolean>. Mounts a
 * <ConfirmDialog> via createRoot, resolves with true on Confirm,
 * false on Cancel/Escape/backdrop-click.
 *
 * Migrated from apps/crm/src/components/ui/ConfirmDialog.tsx →
 * @quotech/ui (qf!2472). Same call signature; the underlying
 * visual is now SAM's themed component instead of CRM's
 * Radix-AlertDialog-with-hard-coded-slate-colors.
 */
export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)

    const cleanup = () => {
      // Defer unmount past the close animation (150ms) so the
      // exit transition can finish without flashing a blank
      // overlay rect.
      setTimeout(() => {
        root.unmount()
        container.remove()
      }, 200)
    }

    const handle = (value: boolean) => () => {
      resolve(value)
      // Re-render with open=false so the close animation can play
      // before unmount.
      root.render(
        <ConfirmDialog
          open={false}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={() => {}}
          onCancel={() => {}}
        />,
      )
      cleanup()
    }

    root.render(
      <ConfirmDialog
        open
        title={options.title}
        description={options.description}
        confirmLabel={options.confirmLabel}
        cancelLabel={options.cancelLabel}
        variant={options.variant}
        onConfirm={handle(true)}
        onCancel={handle(false)}
      />,
    )
  })
}

/**
 * Imperative alert — single OK button, returns Promise<void>.
 * Mounts a confirm-style modal with no Cancel; clicking the
 * confirm button (or backdrop) resolves.
 */
export function alertDialog(options: {
  title: string
  description?: string
  okLabel?: string
}): Promise<void> {
  return new Promise<void>((resolve) => {
    void confirmDialog({
      title: options.title,
      description: options.description,
      confirmLabel: options.okLabel ?? 'OK',
      cancelLabel: options.okLabel ?? 'OK',
    }).then(() => resolve())
  })
}

/* ── Hook API ───────────────────────────────────────────── */

export interface UseConfirmDialogOptions {
  title: string
  description?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  variant?: ConfirmDialogVariant
}

export interface UseConfirmDialogReturn {
  /** Open the dialog and wait for the user's choice.
   * Resolves true on Confirm, false on Cancel/Escape/backdrop. */
  confirm: (options: UseConfirmDialogOptions) => Promise<boolean>
  /** Spread these onto a `<ConfirmDialog>` rendered inside your
   * component tree — keeps the dialog inside the React tree
   * (better for focus management + a11y) instead of mounting a
   * portal-root via the imperative `confirmDialog()` helper. */
  dialogProps: ConfirmDialogProps
}

/**
 * Hook variant of `confirmDialog()` — returns `{ confirm,
 * dialogProps }`. Use this when you want the dialog DOM to live
 * inside your component tree (better for focus management and
 * shared providers) instead of escaping via a createRoot portal.
 *
 * Migrated from apps/quote/src/components/builder/modals/
 * ConfirmDialog.tsx in qf!2482 (L3.2 phase 2).
 *
 * Example:
 *   const { confirm, dialogProps } = useConfirmDialog()
 *   ...
 *   if (await confirm({ title: 'Delete?', variant: 'danger' })) {
 *     await deleteIt()
 *   }
 *   ...
 *   return <>
 *     ...
 *     <ConfirmDialog {...dialogProps} />
 *   </>
 */
export function useConfirmDialog(): UseConfirmDialogReturn {
  const [state, setState] = useState<UseConfirmDialogOptions & { open: boolean }>(
    {
      open: false,
      title: '',
    },
  )

  const resolveRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback(
    (options: UseConfirmDialogOptions): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        resolveRef.current = resolve
        setState({ open: true, ...options })
      })
    },
    [],
  )

  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true)
    resolveRef.current = null
    setState((prev) => ({ ...prev, open: false }))
  }, [])

  const handleCancel = useCallback(() => {
    resolveRef.current?.(false)
    resolveRef.current = null
    setState((prev) => ({ ...prev, open: false }))
  }, [])

  const dialogProps: ConfirmDialogProps = {
    open: state.open,
    title: state.title,
    description: state.description,
    confirmLabel: state.confirmLabel,
    cancelLabel: state.cancelLabel,
    variant: state.variant,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  }

  return { confirm, dialogProps }
}
